import React, { useState, useEffect, useMemo } from 'react';
import { 
  FileText, Download, Filter, Search, X, ChevronDown, 
  TrendingUp, TrendingDown, Calendar, Package, Building2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = process.env.REACT_APP_BACKEND_URL;

// SearchInput Component
const SearchInput = ({ value, onChange, placeholder, className = "" }) => (
  <div className={`relative ${className}`}>
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="pl-10 pr-8"
    />
    {value && (
      <button onClick={() => onChange('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-stone-600">
        <X className="w-4 h-4" />
      </button>
    )}
  </div>
);

export const InventoryStatements = () => {
  const [transactions, setTransactions] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [productFilter, setProductFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Export dropdown state
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [transRes, prodRes, vendRes, invRes] = await Promise.all([
        axios.get(`${API}/api/transactions`, { withCredentials: true }),
        axios.get(`${API}/api/products`, { withCredentials: true }),
        axios.get(`${API}/api/vendors`, { withCredentials: true }),
        axios.get(`${API}/api/inventory`, { withCredentials: true })
      ]);
      setTransactions(transRes.data || []);
      setProducts(prodRes.data || []);
      setVendors(vendRes.data || []);
      setInventory(invRes.data || []);
    } catch (error) {
      toast.error("Failed to fetch statement data");
    } finally {
      setLoading(false);
    }
  };

  // Calculate statement data with running balance and proper opening balance
  const statementData = useMemo(() => {
    // First, get all transactions sorted by date
    let allTransactions = [...transactions].sort((a, b) => 
      new Date(a.transaction_date) - new Date(b.transaction_date)
    );
    
    // Calculate base opening stock from inventory
    let baseOpeningStock = 0;
    if (productFilter) {
      const invItem = inventory.find(i => i.id === productFilter);
      baseOpeningStock = invItem?.opening_stock || 0;
    } else {
      baseOpeningStock = inventory.reduce((sum, i) => sum + (i.opening_stock || 0), 0);
    }
    
    // Calculate opening balance for the date range
    // Opening Balance = Base Opening Stock + transactions BEFORE start date
    let openingBalance = baseOpeningStock;
    let transactionsBeforeStartDate = [];
    let transactionsInRange = [];
    
    if (startDate) {
      const startDateTime = new Date(startDate);
      startDateTime.setHours(0, 0, 0, 0);
      
      allTransactions.forEach(t => {
        const transDate = new Date(t.transaction_date);
        // Apply product filter
        if (productFilter && t.product_id !== productFilter) return;
        // Apply vendor filter
        if (vendorFilter && t.vendor_id !== vendorFilter) return;
        
        if (transDate < startDateTime) {
          transactionsBeforeStartDate.push(t);
        } else {
          transactionsInRange.push(t);
        }
      });
      
      // Calculate opening balance from transactions before start date
      transactionsBeforeStartDate.forEach(t => {
        if (t.transaction_type === 'in') {
          openingBalance += t.quantity;
        } else if (t.transaction_type === 'out') {
          openingBalance -= t.quantity;
        }
      });
    } else {
      // No start date filter - use all transactions
      transactionsInRange = allTransactions.filter(t => {
        if (productFilter && t.product_id !== productFilter) return false;
        if (vendorFilter && t.vendor_id !== vendorFilter) return false;
        return true;
      });
    }
    
    // Apply end date filter
    if (endDate) {
      const endDateTime = new Date(endDate + 'T23:59:59');
      transactionsInRange = transactionsInRange.filter(t => 
        new Date(t.transaction_date) <= endDateTime
      );
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      transactionsInRange = transactionsInRange.filter(t => 
        t.product_name?.toLowerCase().includes(term) ||
        t.vendor_name?.toLowerCase().includes(term) ||
        t.notes?.toLowerCase().includes(term)
      );
    }
    
    // Sort transactions in range by date ascending
    transactionsInRange.sort((a, b) => new Date(a.transaction_date) - new Date(b.transaction_date));
    
    // Calculate running balance starting from opening balance
    let runningBalance = openingBalance;
    const dataWithBalance = transactionsInRange.map(trans => {
      const stockIn = trans.transaction_type === 'in' ? trans.quantity : 0;
      const stockOut = trans.transaction_type === 'out' ? trans.quantity : 0;
      runningBalance = runningBalance + stockIn - stockOut;
      
      return {
        ...trans,
        stock_in: stockIn,
        stock_out: stockOut,
        running_balance: runningBalance,
        description: trans.notes || `${trans.transaction_type === 'in' ? 'Stock In' : 'Stock Out'} - ${trans.product_name}`
      };
    });
    
    return {
      transactions: dataWithBalance,
      openingBalance: openingBalance
    };
  }, [transactions, productFilter, vendorFilter, startDate, endDate, searchTerm, inventory]);

  // Calculate summary
  const summary = useMemo(() => {
    const { transactions: statementTrans, openingBalance } = statementData;
    
    const totalIn = statementTrans.reduce((sum, t) => sum + t.stock_in, 0);
    const totalOut = statementTrans.reduce((sum, t) => sum + t.stock_out, 0);
    const closingBalance = openingBalance + totalIn - totalOut;
    
    return {
      openingBalance,
      totalIn,
      totalOut,
      closingBalance,
      transactionCount: statementTrans.length
    };
  }, [statementData]);

  // Export functions
  const exportToExcel = () => {
    const { transactions: statementTrans } = statementData;
    
    // Create header rows
    const headerRows = [
      ['INVENTORY BALANCE STATEMENT'],
      ['S.P Singh and Sons'],
      [`Generated: ${new Date().toLocaleString()}`],
      [''],
      [`Date Range: ${startDate || 'Beginning'} to ${endDate || 'Present'}`],
      [`Product Filter: ${productFilter ? products.find(p => p.id === productFilter)?.product_name : 'All Products'}`],
      [`Vendor Filter: ${vendorFilter ? vendors.find(v => v.id === vendorFilter)?.vendor_name : 'All Vendors'}`],
      [''],
      ['SUMMARY'],
      [`Opening Balance: ${summary.openingBalance.toFixed(2)} kg`],
      [`Total Stock In: +${summary.totalIn.toFixed(2)} kg`],
      [`Total Stock Out: -${summary.totalOut.toFixed(2)} kg`],
      [`Closing Balance: ${summary.closingBalance.toFixed(2)} kg`],
      [''],
      ['TRANSACTION DETAILS'],
      ['Date', 'Transaction No', 'Product', 'Vendor', 'Description', 'Stock In', 'Stock Out', 'Running Balance']
    ];
    
    // Add opening balance row
    const dataRows = [
      ['', '', '', '', 'Opening Balance (Balance Brought Forward)', '', '', summary.openingBalance.toFixed(2)]
    ];
    
    // Add transaction rows
    statementTrans.forEach(d => {
      dataRows.push([
        new Date(d.transaction_date).toLocaleDateString(),
        d.id?.slice(-8) || '-',
        d.product_name,
        d.vendor_name,
        d.description,
        d.stock_in > 0 ? `+${d.stock_in.toFixed(2)}` : '',
        d.stock_out > 0 ? `-${d.stock_out.toFixed(2)}` : '',
        d.running_balance.toFixed(2)
      ]);
    });
    
    // Add closing balance row
    dataRows.push(['', '', '', '', 'Closing Balance', `+${summary.totalIn.toFixed(2)}`, `-${summary.totalOut.toFixed(2)}`, summary.closingBalance.toFixed(2)]);
    
    const ws = XLSX.utils.aoa_to_sheet([...headerRows, ...dataRows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Statement');
    XLSX.writeFile(wb, `Inventory_Statement_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exported successfully');
    setShowExportMenu(false);
  };

  const exportToCSV = () => {
    const { transactions: statementTrans } = statementData;
    
    const exportData = [
      { Date: '', 'Transaction No': '', Product: '', Vendor: '', Description: 'Opening Balance (Balance Brought Forward)', 'Stock In': '', 'Stock Out': '', 'Running Balance': summary.openingBalance.toFixed(2) },
      ...statementTrans.map(d => ({
        'Date': new Date(d.transaction_date).toLocaleDateString(),
        'Transaction No': d.id?.slice(-8) || '-',
        'Product': d.product_name,
        'Vendor': d.vendor_name,
        'Description': d.description,
        'Stock In': d.stock_in > 0 ? `+${d.stock_in.toFixed(2)}` : '',
        'Stock Out': d.stock_out > 0 ? `-${d.stock_out.toFixed(2)}` : '',
        'Running Balance': d.running_balance.toFixed(2)
      })),
      { Date: '', 'Transaction No': '', Product: '', Vendor: '', Description: 'Closing Balance', 'Stock In': `+${summary.totalIn.toFixed(2)}`, 'Stock Out': `-${summary.totalOut.toFixed(2)}`, 'Running Balance': summary.closingBalance.toFixed(2) }
    ];
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Inventory_Statement_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV exported successfully');
    setShowExportMenu(false);
  };

  const exportToText = () => {
    const { transactions: statementTrans } = statementData;
    
    let text = 'INVENTORY BALANCE STATEMENT\n';
    text += 'S.P Singh and Sons\n';
    text += `Generated: ${new Date().toLocaleString()}\n`;
    text += '='.repeat(80) + '\n\n';
    
    text += `Date Range: ${startDate || 'Beginning'} to ${endDate || 'Present'}\n`;
    text += `Product Filter: ${productFilter ? products.find(p => p.id === productFilter)?.product_name : 'All Products'}\n`;
    text += `Vendor Filter: ${vendorFilter ? vendors.find(v => v.id === vendorFilter)?.vendor_name : 'All Vendors'}\n\n`;
    
    text += '--- SUMMARY ---\n';
    text += `Opening Balance: ${summary.openingBalance.toFixed(2)} kg\n`;
    text += `Total Stock In: +${summary.totalIn.toFixed(2)} kg\n`;
    text += `Total Stock Out: -${summary.totalOut.toFixed(2)} kg\n`;
    text += `Closing Balance: ${summary.closingBalance.toFixed(2)} kg\n\n`;
    
    text += '-'.repeat(80) + '\n';
    text += 'TRANSACTION DETAILS\n';
    text += '-'.repeat(80) + '\n\n';
    
    text += `Opening Balance (Balance Brought Forward): ${summary.openingBalance.toFixed(2)} kg\n\n`;
    
    statementTrans.forEach(t => {
      text += `${new Date(t.transaction_date).toLocaleDateString()} | ${t.product_name} | `;
      text += `${t.stock_in > 0 ? 'IN: +' + t.stock_in.toFixed(2) : 'OUT: -' + t.stock_out.toFixed(2)} | `;
      text += `Balance: ${t.running_balance.toFixed(2)}\n`;
    });
    
    text += '\n' + '-'.repeat(80) + '\n';
    text += `Closing Balance: ${summary.closingBalance.toFixed(2)} kg\n`;
    
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Inventory_Statement_${new Date().toISOString().split('T')[0]}.txt`;
    link.click();
    toast.success('Text file exported successfully');
    setShowExportMenu(false);
  };

  const exportToPDF = () => {
    try {
      const { transactions: statementTrans } = statementData;
      const doc = new jsPDF();
      
      // Header
      doc.setFillColor(77, 124, 15);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.text('INVENTORY BALANCE STATEMENT', 14, 18);
      doc.setFontSize(10);
      doc.text('S.P Singh and Sons', 14, 26);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 33);
      
      // Date Range Info
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      const dateRangeText = `Period: ${startDate || 'Beginning'} to ${endDate || 'Present'}`;
      const productText = `Product: ${productFilter ? products.find(p => p.id === productFilter)?.product_name : 'All Products'}`;
      const vendorText = `Vendor: ${vendorFilter ? vendors.find(v => v.id === vendorFilter)?.vendor_name : 'All Vendors'}`;
      doc.text(dateRangeText, 14, 48);
      doc.text(productText, 80, 48);
      doc.text(vendorText, 140, 48);
      
      // Summary Card
      doc.setFillColor(245, 245, 244);
      doc.rect(14, 54, 182, 28, 'F');
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      
      // Opening Balance
      doc.text('Opening Balance', 20, 62);
      doc.setFontSize(12);
      doc.setTextColor(59, 130, 246); // Blue
      doc.text(`${summary.openingBalance.toFixed(2)} kg`, 20, 70);
      
      // Total Stock In
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.text('Total Stock In', 65, 62);
      doc.setFontSize(12);
      doc.setTextColor(77, 124, 15); // Green
      doc.text(`+${summary.totalIn.toFixed(2)} kg`, 65, 70);
      
      // Total Stock Out
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.text('Total Stock Out', 110, 62);
      doc.setFontSize(12);
      doc.setTextColor(225, 29, 72); // Red
      doc.text(`-${summary.totalOut.toFixed(2)} kg`, 110, 70);
      
      // Closing Balance
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.text('Closing Balance', 155, 62);
      doc.setFontSize(12);
      doc.setTextColor(217, 119, 6); // Amber
      doc.text(`${summary.closingBalance.toFixed(2)} kg`, 155, 70);
      
      // Table data - Add opening balance row first
      const tableData = [
        [
          { content: '', styles: { fontStyle: 'bold' } },
          { content: '', styles: { fontStyle: 'bold' } },
          { content: '', styles: { fontStyle: 'bold' } },
          { content: '', styles: { fontStyle: 'bold' } },
          { content: '', styles: { fontStyle: 'bold', textColor: [59, 130, 246] } },
          { content: '', styles: { fontStyle: 'bold', textColor: [59, 130, 246] } },
          { content: summary.openingBalance.toFixed(2), styles: { fontStyle: 'bold', textColor: [59, 130, 246] } }
        ]
      ];
      
      // Add transaction rows
      statementTrans.forEach(t => {
        tableData.push([
          new Date(t.transaction_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
          t.id?.slice(-8).toUpperCase() || '-',
          t.product_name?.substring(0, 18) || '-',
          t.vendor_name?.substring(0, 15) || '-',
          t.stock_in > 0 ? '+' + t.stock_in.toFixed(2) : '',
          t.stock_out > 0 ? '-' + t.stock_out.toFixed(2) : '',
          t.running_balance.toFixed(2)
        ]);
      });
      
      // Add closing balance row
      tableData.push([
        { content: 'CLOSING BALANCE', colSpan: 4, styles: { fontStyle: 'bold', fillColor: [254, 243, 199] } },
        { content: '+' + summary.totalIn.toFixed(2), styles: { fontStyle: 'bold', textColor: [77, 124, 15], fillColor: [254, 243, 199] } },
        { content: '-' + summary.totalOut.toFixed(2), styles: { fontStyle: 'bold', textColor: [225, 29, 72], fillColor: [254, 243, 199] } },
        { content: summary.closingBalance.toFixed(2), styles: { fontStyle: 'bold', textColor: [217, 119, 6], fillColor: [254, 243, 199] } }
      ]);
      
      autoTable(doc, {
        head: [
          [
            { content: 'Date', styles: { fillColor: [77, 124, 15] } },
            { content: 'Trans No.', styles: { fillColor: [77, 124, 15] } },
            { content: 'Product', styles: { fillColor: [77, 124, 15] } },
            { content: 'Vendor', styles: { fillColor: [77, 124, 15] } },
            { content: 'Stock In', styles: { fillColor: [77, 124, 15] } },
            { content: 'Stock Out', styles: { fillColor: [77, 124, 15] } },
            { content: 'Balance', styles: { fillColor: [77, 124, 15] } }
          ]
        ],
        body: tableData,
        startY: 88,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [77, 124, 15], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [250, 250, 249] },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 22 },
          2: { cellWidth: 40 },
          3: { cellWidth: 35 },
          4: { cellWidth: 22, textColor: [77, 124, 15] },
          5: { cellWidth: 22, textColor: [225, 29, 72] },
          6: { cellWidth: 22, fontStyle: 'bold' }
        },
        didDrawPage: function(data) {
          // Footer
          doc.setFontSize(8);
          doc.setTextColor(128, 128, 128);
          doc.text(`Page ${doc.internal.getNumberOfPages()}`, 14, doc.internal.pageSize.height - 10);
          doc.text('SPS Groups - Inventory Management System', 105, doc.internal.pageSize.height - 10, { align: 'center' });
        }
      });
      
      doc.save(`Inventory_Statement_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported successfully');
      setShowExportMenu(false);
    } catch (error) {
      console.error('PDF Export Error:', error);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  const clearFilters = () => {
    setProductFilter('');
    setVendorFilter('');
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-700"></div>
      </div>
    );
  }

  const { transactions: statementTrans } = statementData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-stone-800">Inventory Statement</h2>
          <p className="text-sm text-stone-500">Bank statement-style inventory balance sheet with opening balance</p>
        </div>
      </div>

      {/* Filters Card */}
      <Card className="border-stone-200">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filters
            </CardTitle>
            {(productFilter || vendorFilter || startDate || endDate || searchTerm) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4">
            <div>
              <Label className="text-xs text-stone-500">Product</Label>
              <Select value={productFilter || 'all'} onValueChange={(val) => setProductFilter(val === 'all' ? '' : val)}>
                <SelectTrigger data-testid="statement-product-filter">
                  <SelectValue placeholder="All Products" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-stone-500">Vendor</Label>
              <Select value={vendorFilter || 'all'} onValueChange={(val) => setVendorFilter(val === 'all' ? '' : val)}>
                <SelectTrigger data-testid="statement-vendor-filter">
                  <SelectValue placeholder="All Vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vendors</SelectItem>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-stone-500">Start Date</Label>
              <Input 
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="statement-start-date"
              />
            </div>
            <div>
              <Label className="text-xs text-stone-500">End Date</Label>
              <Input 
                type="date" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="statement-end-date"
              />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Search</Label>
              <SearchInput 
                value={searchTerm} 
                onChange={setSearchTerm} 
                placeholder="Search..."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-xs text-blue-600 font-medium">Opening Balance</p>
            <p className="text-2xl font-bold text-blue-800">{summary.openingBalance.toFixed(2)}</p>
            <p className="text-xs text-blue-500">kg (as of {startDate || 'beginning'})</p>
          </CardContent>
        </Card>
        <Card className="bg-lime-50 border-lime-200">
          <CardContent className="p-4">
            <p className="text-xs text-lime-600 font-medium flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Total Stock In
            </p>
            <p className="text-2xl font-bold text-lime-800">+{summary.totalIn.toFixed(2)}</p>
            <p className="text-xs text-lime-500">kg</p>
          </CardContent>
        </Card>
        <Card className="bg-rose-50 border-rose-200">
          <CardContent className="p-4">
            <p className="text-xs text-rose-600 font-medium flex items-center gap-1">
              <TrendingDown className="w-3 h-3" /> Total Stock Out
            </p>
            <p className="text-2xl font-bold text-rose-800">-{summary.totalOut.toFixed(2)}</p>
            <p className="text-xs text-rose-500">kg</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <p className="text-xs text-amber-600 font-medium">Closing Balance</p>
            <p className="text-2xl font-bold text-amber-800">{summary.closingBalance.toFixed(2)}</p>
            <p className="text-xs text-amber-500">kg</p>
          </CardContent>
        </Card>
        <Card className="bg-stone-50 border-stone-200">
          <CardContent className="p-4">
            <p className="text-xs text-stone-600 font-medium">Transactions</p>
            <p className="text-2xl font-bold text-stone-800">{summary.transactionCount}</p>
            <p className="text-xs text-stone-500">in period</p>
          </CardContent>
        </Card>
      </div>

      {/* Statement Table */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex justify-between items-center">
            <CardTitle className="text-base">Transaction Details</CardTitle>
            <div className="relative">
              <Button 
                variant="outline" 
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="flex items-center gap-2"
                data-testid="statement-export-btn"
              >
                <Download className="w-4 h-4" />
                Download Statement
                <ChevronDown className="w-4 h-4" />
              </Button>
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border z-20">
                  <div className="px-3 py-2 border-b bg-stone-50">
                    <p className="text-xs text-stone-500 font-medium">Download as</p>
                  </div>
                  <button
                    onClick={exportToPDF}
                    className="w-full px-4 py-2 text-left hover:bg-stone-50 flex items-center gap-2"
                    data-testid="statement-export-pdf"
                  >
                    <FileText className="w-4 h-4 text-red-500" /> 
                    <span>PDF Document</span>
                  </button>
                  <button
                    onClick={exportToExcel}
                    className="w-full px-4 py-2 text-left hover:bg-stone-50 flex items-center gap-2"
                    data-testid="statement-export-excel"
                  >
                    <FileText className="w-4 h-4 text-green-600" /> 
                    <span>Excel Spreadsheet</span>
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="w-full px-4 py-2 text-left hover:bg-stone-50 flex items-center gap-2"
                    data-testid="statement-export-csv"
                  >
                    <FileText className="w-4 h-4 text-blue-500" /> 
                    <span>CSV (Comma Delimited)</span>
                  </button>
                  <button
                    onClick={exportToText}
                    className="w-full px-4 py-2 text-left hover:bg-stone-50 flex items-center gap-2"
                    data-testid="statement-export-text"
                  >
                    <FileText className="w-4 h-4 text-stone-500" /> 
                    <span>Text File</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Trans. No.</TableHead>
                  <TableHead className="font-semibold">Product</TableHead>
                  <TableHead className="font-semibold">Vendor</TableHead>
                  <TableHead className="font-semibold">Description</TableHead>
                  <TableHead className="text-left font-semibold text-lime-700">Stock In</TableHead>
                  <TableHead className="text-left font-semibold text-rose-700">Stock Out</TableHead>
                  <TableHead className="text-left font-semibold">Running Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Opening Balance Row */}
                <TableRow className="bg-blue-50/50 border-b-2 border-blue-200">
                  <TableCell className="font-medium" colSpan={5}>
                    <span className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-blue-600" />
                      Opening Balance {startDate && <span className="text-xs text-stone-500">(Balance as of {startDate})</span>}
                    </span>
                  </TableCell>
                  <TableCell className="text-left">—</TableCell>
                  <TableCell className="text-left">—</TableCell>
                  <TableCell className="text-left font-bold text-blue-700">
                    {summary.openingBalance.toFixed(2)}
                  </TableCell>
                </TableRow>
                
                {statementTrans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-stone-500">
                      No transactions found for the selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  statementTrans.map((trans, idx) => (
                    <TableRow 
                      key={trans.id || idx} 
                      className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'}
                    >
                      <TableCell className="font-medium">
                        {new Date(trans.transaction_date).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-stone-500">
                        {trans.id?.slice(-8).toUpperCase() || '—'}
                      </TableCell>
                      <TableCell>{trans.product_name}</TableCell>
                      <TableCell>{trans.vendor_name}</TableCell>
                      <TableCell className="text-stone-600 text-sm max-w-xs truncate">
                        {trans.description}
                      </TableCell>
                      <TableCell className="text-left font-medium text-lime-700">
                        {trans.stock_in > 0 ? `+${trans.stock_in.toFixed(2)}` : ''}
                      </TableCell>
                      <TableCell className="text-left font-medium text-rose-700">
                        {trans.stock_out > 0 ? `-${trans.stock_out.toFixed(2)}` : ''}
                      </TableCell>
                      <TableCell className="text-left font-bold">
                        {trans.running_balance.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
                
                {/* Closing Balance Row */}
                <TableRow className="bg-amber-50/50 border-t-2 border-amber-200">
                  <TableCell className="font-medium" colSpan={5}>
                    <span className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-amber-600" />
                      Closing Balance
                    </span>
                  </TableCell>
                  <TableCell className="text-left font-bold text-lime-700">
                    +{summary.totalIn.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-left font-bold text-rose-700">
                    -{summary.totalOut.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-left font-bold text-amber-700 text-lg">
                    {summary.closingBalance.toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Footer Info */}
      <div className="flex justify-between items-center text-sm text-stone-500">
        <p>
          Showing {statementTrans.length} transaction(s)
          {(startDate || endDate) && ` | Period: ${startDate || 'Beginning'} to ${endDate || 'Present'}`}
        </p>
        <p>Generated: {new Date().toLocaleString()}</p>
      </div>
    </div>
  );
};

export default InventoryStatements;
